<template>
    <div class="sticky-container">
        <div
            class="upload-hero"
            ref="hero"
            @drop="handleDrop($event)"
            :class="{
                'is-sticky': sticky,
                'is-processing': isUploadProcessing,
                'is-checkout': isCheckout,
                'is-pulsating': isFileDragging,
            }"
            :style="{
                top: (sticky ? -heroHeight + 122 + 71 - 15 : 0) + 'px',
            }"
        >
            <div
                class="upload-hero-background"
                v-if="
                    [
                        orderProcess.STEPS.NO_ORDER_ONGOING,
                        orderProcess.STEPS.ORDER_COMPLETED,
                    ].includes(currentOrderProcessStep)
                "
            >
                <img
                    alt="Hero Banner"
                    v-if="backgroundImage"
                    :src="backgroundImage"
                />
                <figure
                    v-else
                    class="image video"
                    style="position: relative; height: 100%"
                >
                    <video
                        class="has-ratio"
                        ref="backgroundVideo"
                        autoplay="autoplay"
                        muted="muted"
                        loop="loop"
                        preload="true"
                        playsinline
                        style="height: 100%; width: 100%; object-fit: cover"
                    >
                        <source
                            src="https://content.murtfeldt-as.de/fileadmin/redakteure/startseite/Headervideo.mp4"
                            type="video/mp4"
                        />
                    </video>
                </figure>
            </div>
            <input
                @change="processFile(null)"
                type="file"
                id="3d-file"
                hidden
                ref="fileInput"
            />
            <div class="upload-hero-body">
                <div class="container has-text-left" v-if="!isCheckout">
                    <template v-if="!isUploadProcessing">
                        <div class="upload-hero-cta">
                            <div class="upload-hero-title">
                                {{ $t('checkout.upload.hero.title') }}
                            </div>
                            <div class="upload-hero-subtitle">
                                {{ $t('checkout.upload.hero.subtitle') }}
                            </div>
                            <div class="upload-hero-cta-section">
                                <div>
                                    <b-button
                                        @click="selectFile"
                                        type="is-primary-dark"
                                        size="is-medium"
                                        class="uploadButton"
                                        ><img
                                            src="@/assets/img/lnr-upload-white.svg"
                                            class="mr-3"
                                            style="transform: translateY(3px)"
                                        />{{
                                            $t('checkout.upload.choose3DFile')
                                        }}
                                    </b-button>
                                </div>
                                <div>
                                    <b-button
                                        @click="useDemoObject"
                                        type="is-light"
                                        size="is-normal"
                                        style="background-color: #CCFF26;"
                                    >
                                        {{ $t('checkout.upload.startDemo') }}
                                        <SvgIcon
                                            icon="icon--directional_arrow-right"
                                            class="is-dark ml-3"
                                            style="transform: scale(0.6) translateY(5px)"
                                        />
                                    </b-button>
                                </div>
                            </div>
                        </div>
                    </template>
                    <div
                        class="upload-hero-loadingscreen"
                        v-if="isUploadProcessing"
                    >
                        <div class="loading-animation"></div>
                        <div class="loading-status">
                            {{ fileUploadState.message }}
                        </div>
                    </div>
                </div>
            </div>
            <div
                v-if="!isCheckout && !isUploadProcessing"
                class="upload-hero-footer"
                ref="heroFooter"
            >
                <div class="container">
                    <Transition name="fade-wipe-top">
                        <div
                            class="upload-hero-footer-content"
                            v-if="!footerExpanded && sticky"
                        >
                            <div class="" v-if="supportsDragAndDrop">
                                {{ $t('checkout.upload.dragAndDrop3DFile') }}
                            </div>
                            <div class="" v-if="supportsDragAndDrop">
                                {{ $t('checkout.upload.or') }}
                            </div>
                            <a
                                @click="selectFile"
                                class="button is-primary-dark"
                                >{{ $t('checkout.upload.choose3DFile') }}</a
                            >
                        </div>
                    </Transition>
                    <Transition name="fade-wipe-bottom">
                        <div
                            class="upload-hero-footer-content"
                            v-if="footerExpanded && !sticky"
                        >
                            <div class="" v-if="supportsDragAndDrop">
                                {{ $t('checkout.upload.dragAndDrop3DFile') }}
                            </div>
                        </div>
                    </Transition>
                </div>
            </div>
            <div v-if="isUploadProcessing" class="upload-progress">
                <div
                    class="mas-progress"
                    :style="{ width: uploadProgress + '%' }"
                ></div>
            </div>
        </div>
        <div
            class="sticky-container__placeholder"
            ref="stickyPlaceholder"
            v-if="sticky"
            :style="{ height: heroHeight + 'px' }"
        ></div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { orderProcess } from '../../store/constants/order-process';
import { eventBus } from '../../events/event-bus';
import SvgIcon from '../svg/SvgIcon';

export default {
    name: 'UploadHero',
    components: {
        SvgIcon,
    },
    data() {
        return {
            sticky: false,
            heroFooterHeight: 0,
            footerExpanded: true,
            heroHeight: 0,
            isUploadProcessing: false,
            orderProcess: orderProcess,
            supportsDragAndDrop: false,
            isFileDragging: false,
            fileUploadState: '',
            uploadProgress: 0,
        };
    },
    computed: {
        ...mapGetters(['cmsPage', 'screenSize']),
        currentOrderProcessStep: {
            get() {
                return this.$store.getters['currentOrderProcessStep'];
            },
            set(orderProcessStep) {
                this.$store.dispatch(
                    'setCurrentOrderProcessStep',
                    orderProcessStep
                );
            },
        },
        isCheckout() {
            return [orderProcess.STEPS.CONFIGURE_MODEL].includes(
                this.currentOrderProcessStep
            );
        },
        backgroundImage() {
            if (
                this.cmsPage.page &&
                this.cmsPage.page.resources.media.length > 0
            ) {
                return this.cmsPage.page.resources.media[0].publicUrl;
            }
        },
        pageSubtitle() {
            if (this.cmsPage.page) {
                return this.cmsPage.page.meta.subtitle;
            }
            return '';
        },
    },
    methods: {
        setUpEventListeners() {
            let vm = this;
            if (window.FormData && window.FileReader) {
                vm.supportsDragAndDrop = true;

                [
                    'drag',
                    'dragstart',
                    'dragend',
                    'dragover',
                    'dragenter',
                    'dragleave',
                    'drop',
                ].forEach(eventType => {
                    window.addEventListener(eventType, event => {
                        event.preventDefault();
                        event.stopPropagation();
                    });
                });

                ['dragenter', 'dragover'].forEach(eventType => {
                    window.addEventListener(eventType, event => {
                        vm.isFileDragging = vm.dragEventContainFiles(event);
                    });
                });

                ['dragleave', 'drop'].forEach(eventType => {
                    window.addEventListener(eventType, event => {
                        vm.isFileDragging = false;
                    });
                });
            }
        },
        dragEventContainFiles(dragEvent) {
            if (dragEvent.dataTransfer.types) {
                for (let i = 0; i < dragEvent.dataTransfer.types.length; i++) {
                    if (dragEvent.dataTransfer.types[i] === 'Files') {
                        return true;
                    }
                }
            }

            return false;
        },
        handleDrop(event) {
            event.preventDefault();
            this.processFile(event.dataTransfer.files[0]);
        },
        selectFile() {
            let clickEvent = new MouseEvent('click', {
                view: window,
                bubbles: true,
                cancelable: false,
            });
            this.$refs.fileInput.dispatchEvent(clickEvent);
        },
        processFile(file) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            let vm = this;
            vm.isUploadProcessing = true;
            vm.file = file || this.$refs.fileInput.files[0];

            vm.$store
                .dispatch('upload3DFile', { file: vm.file })
                .then(() => {
                    vm.isUploadProcessing = false;
                    vm.currentOrderProcessStep =
                        orderProcess.STEPS.CONFIGURE_MODEL;
                    vm.$store.dispatch('setCheckoutVisibility', true);
                })
                .catch(() => {
                    vm.isUploadProcessing = false;
                    vm.currentOrderProcessStep =
                        orderProcess.STEPS.NO_ORDER_ONGOING;
                });
        },
        useDemoObject() {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            this.isUploadProcessing = true;
            this.fileUploadState = {
                stateFrom: 'uploading',
                stateTo: 'analyzing',
                message: this.$i18n.t('checkout.upload.uploadState.analyzing'),
            };
            setTimeout(() => {
                this.$store.dispatch('useDemoObject').then(() => {
                    this.isUploadProcessing = false;
                    this.currentOrderProcessStep =
                        orderProcess.STEPS.CONFIGURE_MODEL;
                    this.$store.dispatch('setCheckoutVisibility', true);
                });
            }, 3000);
        },
        showFileUploadState(message) {
            this.fileUploadState = message;
        },
        pauseVideo() {
            this.$refs.backgroundVideo.pause();
        },
        playVideo() {
            this.$refs.backgroundVideo.play();
        },
        calcStickyStates() {
            this.calcFooterExpandedState();
            this.calcHeroStickyState();
        },
        calcHeroStickyState() {
            const newState = this.isSticky();

            if (this.sticky === newState) return;

            this.sticky = newState;

            if (this.sticky) {
                this.pauseVideo();
            } else {
                this.playVideo();
            }
        },
        calcFooterExpandedState() {
            const newState =
                window.scrollY < this.heroHeight - this.heroFooterHeight + 67;
            if (this.footerExpanded === newState) return;

            this.footerExpanded = newState;
        },
        isSticky() {
            const scrollPosition = window.pageYOffset;

            return (
                scrollPosition >= this.heroHeight - this.heroFooterHeight + 67
            );
        },
        adjustHeights() {
            if (this.$refs.hero.offsetHeight !== this.heroHeight) {
                this.heroHeight = this.$refs.hero.offsetHeight;
            }
        },
        attachEventListeners() {
            window.addEventListener('scroll', this.calcStickyStates, {
                passive: true,
            });
            window.addEventListener('resize', this.adjustHeights);
        },
        removeEventListeners() {
            window.removeEventListener('scroll', this.calcStickyStates);
            window.removeEventListener('resize', this.adjustHeights);
        },
    },
    created() {
        this.attachEventListeners();
        let vm = this;
        eventBus.$on('fileUploadStateChanged', message => {
            vm.showFileUploadState(message);
        });
        eventBus.$on('fileUploadProgress', progress => {
            vm.uploadProgress = progress;
        });

        eventBus.$on('fileUploadAborted', () => {
            vm.isUploadProcessing = false;
            vm.fileUploadState = '';
            vm.uploadProgress = 0;
        });
    },
    mounted() {
        this.heroHeight = this.$refs.hero.offsetHeight;
        this.heroFooterHeight = this.$refs.heroFooter.offsetHeight;
        this.setUpEventListeners();
    },
    destroyed() {
        this.removeEventListeners();
    },
};
</script>
